// src/pages/PdeReview.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../utilities/ApiConfig";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Typography,
  InputAdornment,
  TableCell,
  Paper,
  Container,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import MUIDataTable from "mui-datatables";
import { useSelector } from "react-redux";
import ApiLoader from "../components/ApiLoader";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SideMenu from "../components/SideMenu";
import Header from "../components/Header";
import UserMenu from "../components/UserMenu";
import Footer from "../components/Footer";

/* ========================= Utils: Base64 -> Blob ========================= */
const normalizeBase64 = (raw) => {
  if (!raw) return "";
  let b64 = String(raw)
    .trim()
    .replace(/^["']|["']$/g, ""); // strip quotes if any
  const i = b64.indexOf("base64,"); // drop data url prefix if present
  if (i !== -1) b64 = b64.slice(i + 7);
  b64 = b64.replace(/\s+/g, ""); // remove whitespace/newlines
  b64 = b64.replace(/-/g, "+").replace(/_/g, "/"); // URL-safe -> standard
  const pad = b64.length % 4;
  if (pad) b64 += "=".repeat(4 - pad); // restore padding
  return b64;
};

const base64PdfToBlob = (b64) => {
  const clean = normalizeBase64(b64);
  const byteChars = atob(clean);
  const sliceSize = 1024 * 512; // 512KB chunks for big files
  const byteArrays = [];
  for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
    const slice = byteChars.slice(offset, offset + sliceSize);
    const nums = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) nums[i] = slice.charCodeAt(i);
    byteArrays.push(new Uint8Array(nums));
  }
  return new Blob(byteArrays, { type: "application/pdf" });
};
/* ======================================================================== */

const PdeReview = () => {
  const [loading, setLoading] = useState(false);
  const [reviewRecords, setReviewRecords] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  // status counts from API
  const [pendingCount, setPendingCount] = useState(0);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [revertedCount, setRevertedCount] = useState(0);

  // pagination state
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // <600
  const isSmMd = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600-900
  const computedRows = isXs ? 5 : isSmMd ? 8 : 10;

  const [page, setPage] = useState(0); // zero-based
  const [pageSize, setPageSize] = useState(computedRows);
  const [totalCount, setTotalCount] = useState(0);

  const searchTimer = useRef(null);

  const navigate = useNavigate();
  const { dcode, srocode } = useSelector(
    (state) => state?.auth?.userDetails || {}
  );

  // keep pageSize updated when viewport changes
  useEffect(() => {
    setPageSize(computedRows);
    setPage(0);
  }, [computedRows]);

  // debounce search input
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      300
    );
    return () => clearTimeout(searchTimer.current);
  }, [searchText]);

  /* ========================= Data Fetch (server-side) ========================= */
  const fetchPdeReviewSummary = async (pageIndex, statusCode) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/ReviewPDE/GetPendingReviewSummary`,
        {
          params: {
            page: pageIndex + 1,
            pageSize,
            status: statusCode ?? null,
            dcode,
            srocode,
            q: debouncedSearch || undefined,
          },
        }
      );

      setPendingCount(data.pendingCount ?? 0);
      setAcceptedCount(data.acceptedCount ?? 0);
      setRevertedCount(data.revertedCount ?? 0);
      setTotalCount(data.totalCount ?? data.TotalCount ?? 0);

      const rows = (data.items || []).map((row) => ({
        preregno: row.pdE_ID, // PDE ID
        docNature: row.deedcode || "",
        subDeed: row.deedsubcode || "",
        location: row.location || "",
        status: row.status || "",
        date: row.updatedOn
          ? new Date(row.updatedOn).toLocaleDateString("en-GB")
          : "",
      }));
      setReviewRecords(rows);
    } catch (error) {
      console.error("Error fetching PDE Review summary:", error);
      toast.error("Failed to fetch PDE summaries");
    } finally {
      setLoading(false);
    }
  };
  /* ============================================================================ */

  /* ========================= Preview Handler (Base64 -> PDF) ========================= */
  const handlePreviewPdf = async (pdeId) => {
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Please allow popups for this site to preview the PDF.");
      return;
    }

    try {
      setLoading(true);

      const url = `${API_BASE_URL}/DSCMail/GetBase64PDF?pde_id=${encodeURIComponent(
        pdeId
      )}`;
      // const url = `https://backendcrs.eregistrationukgov.in/api/DSCMail/GetBase64PDF?pde_id=UKVPD2025045100035`;

      const { data } = await axios.get(url, {
        responseType: "json",
        headers: { Accept: "application/json" },
      });

      // data looks like: { message: "...", base64: "JVBERi0xLjUN..." }
      const b64 = data?.base64;
      if (!b64) {
        w.close();
        toast.error("No PDF returned for this PDE.");
        return;
      }

      const blob = base64PdfToBlob(b64);
      const pdfUrl = URL.createObjectURL(blob);

      w.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Preview Deed - ${String(pdeId || "")}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            html, body { height: 100%; margin: 0; background: #101014; }
            iframe { border: 0; width: 100%; height: 100%; background: #fff; }
          </style>
        </head>
        <body>
          <iframe src="${pdfUrl}" allow="fullscreen"></iframe>
        </body>
      </html>
    `);
      w.document.close();

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 120000);
    } catch (err) {
      console.error(err);
      try {
        w.close();
      } catch {}
      toast.error("Unable to preview the deed PDF.");
    } finally {
      setLoading(false);
    }
  };

  /* ==================================================================================== */

  const countsByStatus = {
    All: (pendingCount || 0) + (acceptedCount || 0) + (revertedCount || 0),
    Pending: pendingCount || 0,
    Accepted: acceptedCount || 0,
    Reverted: revertedCount || 0,
  };

  // filter client-side on current page data (extra safety if backend q not used)
  const filteredRecords = useMemo(() => {
    return reviewRecords.filter((rec) => {
      if (activeTab !== "All" && rec.status !== activeTab) return false;
      if (debouncedSearch) {
        const lower = debouncedSearch.toLowerCase();
        return (
          (rec.preregno || "").toLowerCase().includes(lower) ||
          (rec.docNature || "").toLowerCase().includes(lower) ||
          (rec.subDeed || "").toLowerCase().includes(lower) ||
          (rec.location || "").toLowerCase().includes(lower)
        );
      }
      return true;
    });
  }, [reviewRecords, activeTab, debouncedSearch]);

  const headerCellSx = {
    textTransform: "uppercase",
    fontWeight: 600,
    fontFamily: "Poppins",
    backgroundColor: "#EDEDED",
    color: "#111",
    borderBottom: "1px solid #E5E5E5",
    fontSize: "0.82rem",
  };

  const columns = [
    {
      name: "_sno",
      label: "S.no",
      options: {
        filter: false,
        sort: false,
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRenderLite: (i) => <span>{page * pageSize + i + 1}</span>,
      },
    },
    {
      name: "preregno",
      label: "PDE ID",
      options: {
        filter: false,
        sort: true,
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRender: (v) => (
          <div className="text-center text-xs md:text-sm break-words">{v}</div>
        ),
      },
    },
    {
      name: "docNature",
      label: "Deed Code",
      options: {
        filter: false,
        sort: true,
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRender: (v) => (
          <div className="text-center text-xs md:text-sm">{v}</div>
        ),
      },
    },
    {
      name: "subDeed",
      label: "Deed Subcode",
      options: {
        filter: false,
        sort: true,
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRender: (v) => (
          <div className="text-center text-xs md:text-sm">{v}</div>
        ),
      },
    },
    // {
    //   name: "location",
    //   label: "Location",
    //   options: {
    //     filter: false,
    //     sort: true,
    //     customHeadRender: (m) => (
    //       <TableCell align="center" sx={headerCellSx}>
    //         {m.label}
    //       </TableCell>
    //     ),
    //     customBodyRender: (v) => (
    //       <div className="text-center text-xs md:text-sm">{v}</div>
    //     ),
    //   },
    // },

    {
      name: "location",
      label: "Location",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({
          style: {
            maxWidth: 220, // fixed width
            whiteSpace: "normal", // allow wrapping
            wordBreak: "break-word",
            overflowWrap: "break-word",
            textAlign: "center",
          },
        }),
        setCellHeaderProps: () => ({
          style: {
            maxWidth: 220,
            whiteSpace: "normal",
            textAlign: "center",
          },
        }),
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRender: (v) => (
          <div
            className="text-center text-xs md:text-sm"
            style={{
              maxWidth: 220,
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              margin: "0 auto",
            }}
          >
            {v}
          </div>
        ),
      },
    },

    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRender: (v) => (
          <Chip
            size="small"
            label={v}
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              bgcolor:
                v === "Pending"
                  ? "rgba(251, 191, 36, 0.18)"
                  : v === "Accepted"
                  ? "rgba(34, 197, 94, 0.18)"
                  : "rgba(239, 68, 68, 0.18)",
              color:
                v === "Pending"
                  ? "#92400e"
                  : v === "Accepted"
                  ? "#065f46"
                  : "#7f1d1d",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          />
        ),
      },
    },
    {
      name: "date",
      label: "Updated On",
      options: {
        filter: false,
        sort: true,
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRender: (v) => (
          <div className="text-center text-xs md:text-sm">{v}</div>
        ),
      },
    },
    {
      name: "Preview",
      label: "Preview Deed",
      options: {
        filter: false,
        sort: false,
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRenderLite: (i) => {
          const rec = filteredRecords[i]; // rec.preregno is PDE ID
          return (
            <Button
              variant="contained"
              size="small"
              onClick={() => handlePreviewPdf(rec.preregno)}
            >
              Preview
            </Button>
          );
        },
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customHeadRender: (m) => (
          <TableCell align="center" sx={headerCellSx}>
            {m.label}
          </TableCell>
        ),
        customBodyRenderLite: (i) => (
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              navigate(`/pde-review/${filteredRecords[i].preregno}`)
            }
          >
            View
          </Button>
        ),
      },
    },
  ];

  const options = {
    serverSide: true,
    count: totalCount,
    page,
    rowsPerPage: pageSize,
    rowsPerPageOptions: [pageSize], // lock rows per page per device
    onTableChange: (action, state) => {
      if (action === "changePage") setPage(state.page);
    },
    selectableRows: "none",
    filterType: "dropdown",
    elevation: 0,
    download: false,
    print: false,
    viewColumns: false,
    search: false,
    responsive: "standard",
    rowHover: true,
    setTableProps: () => ({
      sx: {
        tableLayout: "auto",
        "& td, & th": { whiteSpace: "nowrap" },
        "& td:nth-of-type(2)": { maxWidth: 220, whiteSpace: "normal" }, // PDE ID can wrap
      },
    }),
  };

  // Tight, readable theme on small screens
  const appTheme = createTheme({
    typography: { fontFamily: "Poppins, Inter, system-ui, -apple-system" },
    palette: {
      mode: "light",
      background: { paper: "#fff", default: "#f6f7fb" },
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          body: {
            padding: "4px 5px",
            borderBottom: "1px solid #ECECEC",
            textAlign: "center",
            fontSize: isXs ? "0.78rem" : "0.86rem",
          },
          head: {
            padding: "5px",
          },
          footer: { padding: "4px 6px" },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: "none", borderRadius: 8 },
          sizeSmall: { padding: "4px 10px", fontSize: "0.78rem" },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            // minHeight: 36,
            padding: "6px 12px",
            fontSize: isXs ? "0.8rem" : "0.9rem",
          },
        },
      },
    },
  });

  // reload on tab/page or query/filters
  useEffect(() => {
    const code =
      activeTab === "All"
        ? null
        : activeTab === "Pending"
        ? 1
        : activeTab === "Accepted"
        ? 2
        : 3;

    fetchPdeReviewSummary(page, code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, dcode, srocode, debouncedSearch, pageSize]);

  return (
    <>
      {loading && <ApiLoader />}
      <div className="flex flex-row h-screen">
        <SideMenu className="w-full md:w-64" />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="shadow-sm">
            <Header />
          </div>

          <div className="flex flex-1 flex-col md:flex-row overflow-y-auto">
            <div className="flex-1 overflow-auto p-1">
              {/* Sticky toolbar under Header */}
              <Paper
                elevation={1}
                square
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: (t) => t.zIndex.appBar - 1,
                  borderBottom: "1px solid #eee",
                  bgcolor: "#fff",
                }}
              >
                <Container maxWidth="xl" sx={{ py: 1.5 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                      gap: 1.5,
                      alignItems: "center",
                    }}
                  >
                    {/* Title + Search */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
                        gap: 1.25,
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant={isXs ? "h6" : "h5"}
                        fontWeight={600}
                        sx={{ lineHeight: 1.2 }}
                      >
                        PDE Review
                      </Typography>

                      <TextField
                        placeholder="Search PDE ID / Deed / Location"
                        size="small"
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setPage(0);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: "100%" }}
                      />
                    </Box>

                    {/* Tabs */}
                    <Tabs
                      value={activeTab}
                      onChange={(e, nv) => {
                        setActiveTab(nv);
                        setPage(0);
                      }}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        minHeight: 36,
                        "& .MuiTabs-flexContainer": { gap: 0.5 },
                      }}
                    >
                      <Tab
                        value="All"
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <span>All</span>
                            <Chip size="small" label={countsByStatus.All} />
                          </Box>
                        }
                      />
                      <Tab
                        value="Pending"
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <span>Pending</span>
                            <Chip
                              size="small"
                              label={countsByStatus.Pending}
                              color="warning"
                            />
                          </Box>
                        }
                      />
                      <Tab
                        value="Accepted"
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <span>Accepted</span>
                            <Chip
                              size="small"
                              label={countsByStatus.Accepted}
                              color="success"
                            />
                          </Box>
                        }
                      />
                      <Tab
                        value="Reverted"
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <span>Reverted</span>
                            <Chip
                              size="small"
                              label={countsByStatus.Reverted}
                              color="error"
                            />
                          </Box>
                        }
                      />
                    </Tabs>
                  </Box>
                </Container>
              </Paper>

              {/* CONTENT */}
              <Box sx={{ flex: 1, minHeight: "auto" }}>
                <Container maxWidth="xl" sx={{ py: { xs: 1, md: 2 } }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    <ThemeProvider theme={appTheme}>
                      <MUIDataTable
                        title=""
                        data={filteredRecords}
                        columns={columns}
                        options={options}
                      />
                    </ThemeProvider>
                  </Box>
                </Container>
              </Box>
            </div>
          </div>

          {/* Footer (Spans Only MainContent) */}
          <div className="p-0 bg-blue-100 text-center">
            <Footer />
          </div>
        </div>

        {/* Right Sidebar (Hidden on Mobile, Visible on Medium Screens and Above) */}
        <div className="hidden md:block w-64 h-full">
          <UserMenu />
        </div>

        {/* Mobile Bottom Navigation (Visible on Mobile, Hidden on Medium Screens and Above) */}
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg md:hidden">
          <div className="flex justify-around p-2">
            {/* Mobile Menu Items */}
            <button
              onClick={() => {
                /* Toggle Left Sidebar */
              }}
            >
              {/* Icon for Menu */}
            </button>
            <button
              onClick={() => {
                /* Toggle Right Sidebar */
              }}
            >
              {/* Icon for User Menu */}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PdeReview;
